import { useState } from 'react';
import { Pencil, Tag, Clock, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Avatar } from '../ui/Avatar';
import { Button } from '../ui/Button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/Tooltip';
import { CollaboratorRoleBadge } from './CollaboratorRoleBadge';
import { CollaboratorLink } from './CollaboratorLink';
import {
  useUpdateCollaborator,
  useActivateCollaborator,
  useDeactivateCollaborator,
  useResendCollaboratorInvitation,
} from '../../hooks/queries/useCollaborators';
import type { CollaboratorActivity } from '../../hooks/queries/useCollaboratorActivity';
import { formatDuration } from '../../services/timeTracking/timeTrackingService';
import { cn } from '../../lib/cn';
import type { CollaboratorWithCategories } from '../../services/collaborators/collaboratorService';

interface CollaboratorRowProps {
  collaborator: CollaboratorWithCategories;
  onEdit: (collaborator: CollaboratorWithCategories) => void;
  index?: number;
  /** Affiche les colonnes "Dernière connexion" et "Aujourd'hui" (admins). */
  showActivity?: boolean;
  activity?: CollaboratorActivity | null;
}

export function CollaboratorRow({ collaborator, onEdit, index, showActivity = false, activity = null }: CollaboratorRowProps) {
  const { mutateAsync: updateCollaborator } = useUpdateCollaborator();
  const { mutateAsync: activate } = useActivateCollaborator();
  const { mutateAsync: deactivate } = useDeactivateCollaborator();
  const { mutateAsync: resendInvitation, isPending: isResending } = useResendCollaboratorInvitation();
  const [isTogglingActive, setIsTogglingActive] = useState(false);

  const isPending = collaborator.auth_user_id === null;

  async function handleRoleChange(newRole: string) {
    const confirmed = window.confirm(
      `Changer le rôle de ${collaborator.full_name} vers "${newRole}" ?`,
    );
    if (!confirmed) return;

    try {
      await updateCollaborator({ id: collaborator.id, role: newRole });
      toast.success('Rôle mis à jour');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur';
      toast.error(`Erreur : ${message}`);
    }
  }

  async function handleToggleActive() {
    setIsTogglingActive(true);
    try {
      if (collaborator.is_active) {
        await deactivate(collaborator.id);
        toast.success(`${collaborator.full_name} désactivé`);
      } else {
        await activate(collaborator.id);
        toast.success(`${collaborator.full_name} activé`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur';
      toast.error(`Erreur : ${message}`);
    } finally {
      setIsTogglingActive(false);
    }
  }

  async function handleResendInvitation() {
    try {
      await resendInvitation(collaborator.id);
      toast.success(`Invitation renvoyée à ${collaborator.email}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur';
      toast.error(`Erreur : ${message}`);
    }
  }

  const visibleCategories = collaborator.categories.slice(0, 3);
  const extraCount = collaborator.categories.length - 3;

  return (
    <motion.tr
      className="hover:bg-neutral-50 transition-colors group"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut', delay: Math.min((index ?? 0) * 0.05, 0.3) }}
    >
      {/* Avatar + Nom */}
      <td className="px-2.5 py-2.5 first:pl-[18px] last:pr-[18px] whitespace-nowrap">
        <div className="flex items-center gap-3">
          <CollaboratorLink collaboratorId={collaborator.id} className="inline-flex flex-shrink-0">
            <Avatar name={collaborator.full_name} src={collaborator.avatar_url} size="sm" />
          </CollaboratorLink>
          <div>
            <CollaboratorLink collaboratorId={collaborator.id} className="hover:underline">
              <p className="text-[15px] font-semibold leading-tight text-neutral-900">{collaborator.full_name}</p>
            </CollaboratorLink>
            <a
              href={`mailto:${collaborator.email}`}
              className="block text-xs text-neutral-500 hover:text-cime-primary hover:underline"
            >
              {collaborator.email}
            </a>
            {isPending && (
              <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                <Clock className="w-3 h-3" aria-hidden />
                En attente
              </span>
            )}
          </div>
        </div>
      </td>

      {/* Rôle */}
      <td className="px-2.5 py-2.5 first:pl-[18px] last:pr-[18px] whitespace-nowrap">
        <select
          value={collaborator.role}
          onChange={(e) => handleRoleChange(e.target.value)}
          className="text-sm rounded-md border border-neutral-200 bg-transparent px-2.5 py-1.5 text-neutral-700 focus:outline-none focus:ring-1 focus:ring-cime-primary cursor-pointer"
          aria-label={`Rôle de ${collaborator.full_name}`}
        >
          <option value="admin">Admin</option>
          <option value="lawyer">Avocat</option>
          <option value="secretary">Assistant(e)</option>
        </select>
        <span className="sr-only">
          <CollaboratorRoleBadge role={collaborator.role} />
        </span>
      </td>

      {/* Compétences */}
      <td className="px-2.5 py-2.5 first:pl-[18px] last:pr-[18px]">
        <div className="flex flex-wrap gap-1.5">
          {visibleCategories.map((cat) => (
            <span
              key={cat.id}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-cime-primary/10 text-cime-primary"
            >
              <Tag className="w-3 h-3" aria-hidden />
              {cat.label}
            </span>
          ))}
          {extraCount > 0 && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-neutral-100 text-neutral-500">
              +{extraCount}
            </span>
          )}
          {collaborator.categories.length === 0 && (
            <span className="text-xs text-neutral-400 italic">Aucune</span>
          )}
        </div>
      </td>

      {/* Temps passé aujourd'hui (admins) */}
      {showActivity && (
        <td className="px-2.5 py-2.5 first:pl-[18px] last:pr-[18px] whitespace-nowrap">
            {activity && activity.seconds_today > 0 ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-sm font-medium text-neutral-700">
                    {formatDuration(Math.round(activity.seconds_today / 60))}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {formatDuration(Math.round(activity.seconds_last7 / 60))} sur 7 jours
                </TooltipContent>
              </Tooltip>
            ) : (
              <span className="text-sm text-neutral-400">—</span>
            )}
        </td>
      )}

      {/* Statut */}
      <td className="px-2.5 py-2.5 first:pl-[18px] last:pr-[18px] whitespace-nowrap">
        <button
          type="button"
          role="switch"
          aria-checked={collaborator.is_active}
          disabled={isTogglingActive}
          onClick={handleToggleActive}
          title={collaborator.is_active ? 'Désactiver' : 'Activer'}
          className={cn(
            'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cime-primary focus-visible:ring-offset-2',
            'disabled:cursor-wait',
            collaborator.is_active ? 'bg-cime-primary' : 'bg-neutral-300',
          )}
        >
          <span
            className={cn(
              'inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform',
              collaborator.is_active ? 'translate-x-6' : 'translate-x-1',
            )}
          />
          <span className="sr-only">{collaborator.is_active ? 'Actif' : 'Inactif'}</span>
        </button>
      </td>

      {/* Dernière connexion (admins) : toujours la dernière colonne */}
      {showActivity && (
          <td className="px-2.5 py-2.5 first:pl-[18px] last:pr-[18px] whitespace-nowrap">
            {activity?.last_login_at ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-sm text-neutral-600">
                    {formatDistanceToNow(new Date(activity.last_login_at), { addSuffix: true, locale: fr })}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {new Date(activity.last_login_at).toLocaleString('fr-FR')}
                </TooltipContent>
              </Tooltip>
            ) : (
              <span className="text-sm text-neutral-400 italic">Jamais</span>
            )}
          </td>
      )}

      {/* Actions */}
      <td className="px-2.5 py-2.5 first:pl-[18px] last:pr-[18px] whitespace-nowrap">
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {isPending && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResendInvitation}
                  disabled={isResending}
                  aria-label={`Renvoyer l'invitation à ${collaborator.full_name}`}
                >
                  <Mail className="w-4 h-4" aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">Renvoyer l'invitation</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(collaborator)}
                aria-label={`Modifier ${collaborator.full_name}`}
              >
                <Pencil className="w-4 h-4" aria-hidden />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Modifier</TooltipContent>
          </Tooltip>
        </div>
      </td>
    </motion.tr>
  );
}
